/**
 * KYC Address Info Mock Data Generator
 *
 * 该模块提供 KYC 地址信息表单的模拟数据生成功能
 * 使用 @faker-js/faker 生成符合表单验证规则的测试数据
 *
 * @module mockDataGenerator.address
 * @description 地址信息模拟数据生成器
 */

import { AddressProofTypeOptions } from "@/types/enum";
import { faker } from "@faker-js/faker";

/* ============================================================================
 * 类型定义
 * ========================================================================== */

/**
 * 国家数据类型
 */
interface CountryData {
  code2: string;
  cn: string;
  en: string;
  hk: string;
  tel: string;
  url: string;
  priority: number;
}

/**
 * 国家JSON数据结构
 */
interface CountryJsonData {
  code: string;
  msg: string;
  data: CountryData[];
}

// 导入真实的国家数据（地址信息使用允许的国家列表）
import countryAllowedDataJson from "../../data/country-allowed.json";
const countryAllowedData = countryAllowedDataJson as CountryJsonData;

/**
 * 地址信息表单数据类型
 */
export interface AddressInfoFormData {
  /** 现居住国家/地区（ISO 3166-1 alpha-3 代码） */
  currentCountry: string;
  /** 居住证明文件类型 */
  residenceProofType: string;
  /** 居住证明文件（文件ID数组） */
  residenceProofFile: string[];
}

/**
 * Mock 数据生成配置选项
 */
export interface AddressMockDataGeneratorOptions {
  /** 指定国家/地区（默认随机） */
  currentCountry?: string;
  /** 指定居住证明类型（默认随机） */
  residenceProofType?: string;
  /** 自定义种子，用于生成可重复的数据 */
  seed?: number;
}

/* ============================================================================
 * 常量配置
 * ========================================================================== */

/**
 * 允许的国家列表（来自 country-allowed.json）
 * 地址信息的currentCountry字段使用允许的国家列表
 */
const ALLOWED_COUNTRIES = countryAllowedData.data;

/**
 * 允许的国家代码列表
 */
const COUNTRY_CODES = ALLOWED_COUNTRIES.map((c: CountryData) => c.code2);

/**
 * 居住证明文件类型选项（来自 strapi-enum.ts）
 */
export const RESIDENCE_PROOF_TYPES = Object.values(AddressProofTypeOptions);

/* ============================================================================
 * 主函数
 * ========================================================================== */

/**
 * 生成地址信息表单模拟数据
 *
 * @param options - 生成配置选项
 * @returns 符合表单验证规则的模拟数据
 *
 * @example
 * ```typescript
 * // 生成基本地址信息
 * const basicData = generateAddressInfoMockData();
 *
 * // 生成指定国家的数据
 * const hkData = generateAddressInfoMockData({
 *   currentCountry: 'HKG'
 * });
 *
 * // 生成可重复的数据（用于测试）
 * const repeatableData = generateAddressInfoMockData({
 *   seed: 12345
 * });
 * ```
 */
export function generateAddressInfoMockData(
  options: AddressMockDataGeneratorOptions = {}
): AddressInfoFormData {
  const {
    currentCountry: specifiedCountry,
    residenceProofType: specifiedProofType,
    seed,
  } = options;

  // 创建 faker 实例（如果提供种子，使用种子）
  const fakerInstance = seed !== undefined ? faker : faker;
  if (seed !== undefined) {
    fakerInstance.seed(seed);
  }

  // 确定国家/地区（从允许的国家列表中随机选择）
  const currentCountry =
    specifiedCountry || fakerInstance.helpers.arrayElement(COUNTRY_CODES);

  // 确定居住证明类型
  const residenceProofType =
    specifiedProofType ||
    fakerInstance.helpers.arrayElement(RESIDENCE_PROOF_TYPES);

  // 文件通过Demo面板触发上传控件来真实上传，不需要生成假数据
  const residenceProofFile: any[] = [];

  return {
    currentCountry,
    residenceProofType,
    residenceProofFile,
  };
}

/**
 * 批量生成地址信息表单模拟数据
 *
 * @param count - 生成数据的数量
 * @param options - 生成配置选项
 * @returns 模拟数据数组
 *
 * @example
 * ```typescript
 * // 生成 10 条测试数据
 * const mockDataList = generateAddressInfoMockDataBatch(10);
 *
 * // 生成 5 条香港地址数据
 * const hkDataList = generateAddressInfoMockDataBatch(5, {
 *   currentCountry: 'HKG'
 * });
 * ```
 */
export function generateAddressInfoMockDataBatch(
  count: number,
  options: AddressMockDataGeneratorOptions = {}
): AddressInfoFormData[] {
  return Array.from({ length: count }, (_, index) => {
    // 如果提供了种子，为每条数据使用递增的种子
    const itemOptions =
      options.seed !== undefined
        ? { ...options, seed: options.seed + index }
        : options;

    return generateAddressInfoMockData(itemOptions);
  });
}

/**
 * 生成特定场景的模拟数据
 *
 * 提供一些预设场景，方便快速生成特定类型的测试数据
 */
export const addressMockDataPresets = {
  /**
   * 香港居民（水电煤账单）
   */
  hkResidentWithUtilityBill: (): AddressInfoFormData =>
    generateAddressInfoMockData({
      currentCountry: "HKG",
      residenceProofType: AddressProofTypeOptions.UTILITY_BILL,
    }),

  /**
   * 中国大陆居民（银行对账单）
   */
  mainlandChineseWithBankStatement: (): AddressInfoFormData =>
    generateAddressInfoMockData({
      currentCountry: "CHN",
      residenceProofType: AddressProofTypeOptions.FINANCIAL_STATEMENT,
    }),

  /**
   * 外国居民（租赁合同）
   */
  foreignResidentWithRental: (): AddressInfoFormData =>
    generateAddressInfoMockData({
      currentCountry: faker.helpers.arrayElement(["USA", "GBR", "JPN", "SGP"]),
      residenceProofType: AddressProofTypeOptions.RESIDENTIAL_LEASE,
    }),

  /**
   * 酒店公寓租赁
   */
  hotelApartmentLease: (): AddressInfoFormData =>
    generateAddressInfoMockData({
      residenceProofType: AddressProofTypeOptions.HOTEL_APARTMENT_LEASE,
    }),
};

/* ============================================================================
 * 导出
 * ========================================================================== */

export default {
  generateAddressInfoMockData,
  generateAddressInfoMockDataBatch,
  addressMockDataPresets,
  RESIDENCE_PROOF_TYPES,
};
